import { setApplicationFeatures } from '../config/applicationFeatures';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { registerSessionInvalidationHandler } from '../api/apiClient';
import type { ApiError } from '../api/apiError';
import { isKioskMode } from '../config/kioskConfig';
import * as authService from '../services/authService';
import type { ActiveRole, AssignedRole, AuthUser } from '../services/authService';
import { clearActiveRole, saveActiveRole } from '../services/activeRoleStore';
import { resetToMainTabs } from '../navigation/navigationRef';
import {
  resumeKioskSessionAfterEmployeeLogout,
  suspendKioskTokensForEmployeeLogin,
} from '../services/kioskAuthService';
import {
  clearTokens,
  getAccessToken,
  getRefreshToken,
  saveTokens,
} from '../services/secureTokenService';
import {
  hasAnyPermission as canAny,
  hasPermission as can,
} from '../utils/permissions';
import { getLocationTracker } from '../services/locationTracker';

export type AuthStatus = 'initializing' | 'authenticated' | 'unauthenticated';

type AuthState = {
  status: AuthStatus;
  user: AuthUser | null;
  employee: null;
  roles: string[];
  assignedRoles: AssignedRole[];
  activeRole: ActiveRole | null;
  permissions: string[];
  sessionMessage: string | null;
};

type AuthContextValue = AuthState & {
  signIn: (loginName: string, password: string) => Promise<void>;
  restoreSession: () => Promise<void>;
  signOut: () => Promise<void>;
  reloadCurrentUser: () => Promise<void>;
  switchRole: (roleCode: string) => Promise<void>;
  changePassword: (input: {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
  }) => Promise<void>;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: readonly string[]) => boolean;
  hasRole: (role: string) => boolean;
  clearSessionMessage: () => void;
};

const emptyState: AuthState = {
  status: 'initializing',
  user: null,
  employee: null,
  roles: [],
  assignedRoles: [],
  activeRole: null,
  permissions: [],
  sessionMessage: null,
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(emptyState);

  const becomeUnauthenticated = useCallback((message: string | null = null) => {
    setApplicationFeatures(null);
    clearActiveRole().catch(() => undefined);
    setState({ ...emptyState, status: 'unauthenticated', sessionMessage: message });
  }, []);

  const applyCurrentUser = useCallback(
    (current: Awaited<ReturnType<typeof authService.getCurrentUser>>) => {
      setState({
        status: 'authenticated',
        user: current.user,
        employee: null,
        roles: current.roles,
        assignedRoles: current.assignedRoles,
        activeRole: current.activeRole,
        permissions: current.permissions,
        sessionMessage: null,
      });
    },
    [],
  );

  const rememberResolvedRole = useCallback(
    async (current: Awaited<ReturnType<typeof authService.getCurrentUser>>) => {
      const roleCode = current.activeRole?.roleCode || current.roles[0];
      if (current.user?.userId && roleCode) {
        await saveActiveRole(current.user.userId, roleCode);
      }
    },
    [],
  );

  const reloadCurrentUser = useCallback(async () => {
    const current = await authService.getCurrentUser();
    applyCurrentUser(current);
    await rememberResolvedRole(current);
  }, [applyCurrentUser, rememberResolvedRole]);

  const restoreSession = useCallback(async () => {
    setState(current => ({ ...current, status: 'initializing' }));
    try {
      const token = await getAccessToken();
      if (!token) {
        const refreshToken = await getRefreshToken();
        if (!refreshToken) {
          becomeUnauthenticated();
          return;
        }
        const refreshed = await authService.refreshSession(refreshToken);
        await saveTokens({
          accessToken: refreshed.accessToken,
          refreshToken: refreshed.refreshToken,
        });
      }
      const current = await authService.getCurrentUser();
      applyCurrentUser(current);
      await rememberResolvedRole(current);
    } catch {
      await clearTokens().catch(() => undefined);
      await clearActiveRole().catch(() => undefined);
      becomeUnauthenticated();
    }
  }, [applyCurrentUser, becomeUnauthenticated, rememberResolvedRole]);

  useEffect(() => {
    registerSessionInvalidationHandler(message => becomeUnauthenticated(message));
    restoreSession();
    return () => registerSessionInvalidationHandler(null);
  }, [becomeUnauthenticated, restoreSession]);

  const signIn = useCallback(
    async (loginName: string, password: string) => {
      await clearActiveRole();
      const session = await authService.login({ loginName, password });
      await saveTokens({
        accessToken: session.accessToken,
        refreshToken: session.refreshToken,
      });
      // Employee session takes over — keep Keychain kiosk session for logout resume.
      if (isKioskMode()) {
        suspendKioskTokensForEmployeeLogin();
      }
      try {
        const current = await authService.getCurrentUser();
        applyCurrentUser(current);
        await rememberResolvedRole(current);
      } catch (error) {
        await clearTokens().catch(() => undefined);
        await clearActiveRole().catch(() => undefined);
        throw error;
      }
    },
    [applyCurrentUser, rememberResolvedRole],
  );

  const switchRole = useCallback(
    async (roleCode: string) => {
      const current = await authService.switchActiveRole(roleCode);
      await rememberResolvedRole(current);
      applyCurrentUser(current);
      resetToMainTabs();
    },
    [applyCurrentUser, rememberResolvedRole],
  );

  const signOut = useCallback(async () => {
    let logoutError: ApiError | null = null;
    // Stop recording and upload what is queued while the session is still valid,
    // without holding up sign-out on a slow network.
    await Promise.race([
      getLocationTracker()
        .stop()
        .catch(() => undefined),
      new Promise(resolve => setTimeout(resolve, 5000)),
    ]);
    try {
      await authService.logout();
    } catch (error) {
      logoutError = error as ApiError;
    } finally {
      await clearTokens().catch(() => undefined);
      await clearActiveRole().catch(() => undefined);
      if (isKioskMode()) {
        await resumeKioskSessionAfterEmployeeLogout().catch(() => undefined);
      }
      becomeUnauthenticated();
    }
    if (logoutError && !['INVALID_SESSION', 'ACCOUNT_NOT_AVAILABLE'].includes(logoutError.code ?? '')) {
      throw logoutError;
    }
  }, [becomeUnauthenticated]);

  const changePassword = useCallback(
    (input: Parameters<typeof authService.changePassword>[0]) =>
      authService.changePassword(input),
    [],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      signIn,
      restoreSession,
      signOut,
      reloadCurrentUser,
      switchRole,
      changePassword,
      hasPermission: permission => can(state.permissions, permission),
      hasAnyPermission: permissions => canAny(state.permissions, permissions),
      hasRole: role => state.roles.includes(role),
      clearSessionMessage: () =>
        setState(current => ({ ...current, sessionMessage: null })),
    }),
    [changePassword, reloadCurrentUser, restoreSession, signIn, signOut, state, switchRole],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside AuthProvider');
  return context;
}
